import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Plus, TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Branch } from '@/lib/types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

interface BranchLicence {
  used: number
  limit: number
  canAddMore: boolean
}

/**
 * Branches, bounded by the cap on the plan. The cap lives in the platform's control database and
 * is enforced on the server; this screen only reflects it, and a 402 is what actually stops an
 * extra branch being created.
 */
export function BranchesSection() {
  const queryClient = useQueryClient()
  const canManageSettings = useSession((state) => state.can)(Permission.ManageSettings)

  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [error, setError] = useState<string | null>(null)

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiClient.get<Branch[]>('/api/branches'),
  })

  const licence = useQuery({
    queryKey: ['branches', 'licence'],
    queryFn: () => apiClient.get<BranchLicence>('/api/branches/licence'),
  })

  const createBranch = useMutation({
    mutationFn: (body: { name: string; address: string | null }) =>
      apiClient.post<Branch>('/api/branches', { ...body, phone: null, receiptPrefix: null }),
    onSuccess: async () => {
      setName('')
      setAddress('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['branches'] })
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not create the branch.',
      ),
  })

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    createBranch.mutate({ name: name.trim(), address: address.trim() || null })
  }

  const atLimit = licence.data && !licence.data.canAddMore

  return (
    <div>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">
        {t('Every branch trades under your store name and appears that way on receipts and reports.')}
      </p>

      {licence.data && (
        <div className="surface-raised mb-5 flex items-center justify-between rounded-[var(--radius-card)] border p-4">
          <div>
            <p className="text-sm font-medium">{t('Branches on your plan')}</p>
            <p className="text-sm text-[var(--text-secondary)]">
              {t('Using {used} of {limit}', { used: licence.data.used, limit: licence.data.limit })}
            </p>
          </div>
          <div className="surface-sunken tabular rounded-lg px-3 py-2 text-lg font-semibold">
            {licence.data.used}/{licence.data.limit}
          </div>
        </div>
      )}

      <ul className="mb-5 flex flex-col gap-2">
        {branches.data?.map((branch) => (
          <li
            key={branch.id}
            className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-4"
          >
            <div className="surface-sunken flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
              <Building2 className="h-5 w-5 text-[var(--text-secondary)]" />
            </div>
            <div className="min-w-0 flex-1">
              {/* Rendered from displayName, never from name alone. */}
              <p className="truncate font-medium">{branch.displayName}</p>
              {branch.address && (
                <p className="truncate text-sm text-[var(--text-secondary)]">{branch.address}</p>
              )}
            </div>
            {branch.isPrimary && (
              <span className="surface-sunken rounded-full px-2.5 py-1 text-xs text-[var(--text-secondary)]">
                {t('Main')}
              </span>
            )}
          </li>
        ))}
      </ul>

      {!canManageSettings ? null : atLimit ? (
        <div className="flex items-start gap-3 rounded-[var(--radius-card)] border border-warn-500/40 bg-warn-500/10 p-4">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-warn-500" />
          <div>
            <p className="text-sm font-medium">{t('Branch limit reached')}</p>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Your plan covers {licence.data?.limit}{' '}
              {licence.data?.limit === 1 ? 'branch' : 'branches'}. Contact your provider to add
              more.
            </p>
          </div>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="surface-raised flex flex-col gap-4 rounded-[var(--radius-card)] border p-4"
        >
          <p className="text-sm font-medium">{t('Add a branch')}</p>

          <Input
            label={t('Branch name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('Hamra')}
            required
          />

          <Input
            label={t('Address')}
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            placeholder={t('Optional')}
          />

          {error && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}

          <Button type="submit" disabled={createBranch.isPending || name.trim().length === 0}>
            <Plus className="h-4 w-4" />
            {t('Add branch')}
          </Button>
        </form>
      )}
    </div>
  )
}
